import React, { useReducer, useCallback } from 'react';
import { TFileId, IFileData, TLanguageIDs, IMenuTranslation, ICreateVideo, IItemForGetShortInfo } from 'types';
import {
	createVideoInitialState,
	CreateVideoContext,
	TVisibleForm,
	TSelectedItem,
	TTypeOfModalWindow,
} from './createVideoContext';
import { createVideoReducer } from './createVideoReducer';
import { CreateVideoActionTypes } from './createVideoActions';

const CreateVideoState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(createVideoReducer, createVideoInitialState);

	const changeVisibleForm = useCallback((value: TVisibleForm) => {
		dispatch({
			type: CreateVideoActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	}, []);

	const setFileId = useCallback((value: TFileId) => {
		dispatch({
			type: CreateVideoActionTypes.SET_FILE_ID,
			payload: {
				fileId: value,
			},
		});
	}, []);

	const setFetchedStudios = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateVideoActionTypes.SET_FETCHED_STUDIOS,
			payload: {
				fetchedStudios: value,
			},
		});
	}, []);

	const setIDStudio = useCallback((value: TSelectedItem) => {
		dispatch({
			type: CreateVideoActionTypes.SET_ID_STUDIO,
			payload: {
				IDSelectedStudio: value,
			},
		});
	}, []);

	const refetchStudios = useCallback((value: boolean) => {
		dispatch({
			type: CreateVideoActionTypes.REFETCH_STUDIOS,
			payload: {
				needRefetchStudios: value,
			},
		});
	}, []);

	const changeNameOfVideo = useCallback((value: string) => {
		dispatch({
			type: CreateVideoActionTypes.CHANGE_NAME_OF_VIDEO,
			payload: {
				nameOfVideo: value,
			},
		});
	}, []);

	const setFetchedCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateVideoActionTypes.SET_FETCHED_CATEGORIES,
			payload: {
				fetchedCategories: value,
			},
		});
	}, []);

	const setIDCategory = useCallback((value: TSelectedItem) => {
		dispatch({
			type: CreateVideoActionTypes.SET_ID_CATEGORY,
			payload: {
				IDSelectedCategory: value,
			},
		});
	}, []);

	const changeFileData = useCallback((value: IFileData) => {
		dispatch({
			type: CreateVideoActionTypes.CHANGE_FILE_DATA,
			payload: {
				fileData: value,
			},
		});
	}, []);

	const setFetchedLanguages = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateVideoActionTypes.SET_FETCHED_LANGUAGES,
			payload: {
				fetchedLanguages: value,
			},
		});
	}, []);

	const setIDsLanguages = useCallback(
		(value: TLanguageIDs[]) => {
			dispatch({
				type: CreateVideoActionTypes.SET_IDs_LANGUAGES,
				payload: {
					IDsSelectedLanguages: value,
				},
			});

			const isNewLanguageAdded = value.length > state.translations.length;
			if (isNewLanguageAdded) {
				const existingTranslationIDs = state.translations.map((el) => el.languageId);
				const newTranslationID = value.find((el) => !existingTranslationIDs.includes(el));
				if (newTranslationID) {
					const newTranslation: IMenuTranslation = {
						languageId: newTranslationID,
						name: '',
					};
					const updatedTranslations = [...state.translations, newTranslation];
					dispatch({
						type: CreateVideoActionTypes.CHANGE_TRANSLATION,
						payload: {
							translations: updatedTranslations,
						},
					});
				}
			} else {
				const updatedTranslations = state.translations.filter((el) => value.includes(el.languageId));
				dispatch({
					type: CreateVideoActionTypes.CHANGE_TRANSLATION,
					payload: {
						translations: updatedTranslations,
					},
				});
			}
		},
		[state.translations],
	);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: CreateVideoActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: CreateVideoActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const setDataForSubmit = useCallback((value: ICreateVideo | null) => {
		dispatch({
			type: CreateVideoActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: CreateVideoActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: CreateVideoActionTypes.RESET_FIELDS }), []);

	return (
		<CreateVideoContext.Provider
			value={{
				changeVisibleForm,
				setFileId,
				setFetchedStudios,
				setIDStudio,
				refetchStudios,
				changeNameOfVideo,
				setFetchedCategories,
				setIDCategory,
				changeFileData,
				setFetchedLanguages,
				setIDsLanguages,
				setTranslationTab,
				changeNameTranslation,
				setDataForSubmit,
				changeTypeOfModalWindow,
				resetFields,
				visibleForm: state.visibleForm,
				fileId: state.fileId,
				sourceUrlLocally: state.sourceUrlLocally,
				fetchedStudios: state.fetchedStudios,
				IDSelectedStudio: state.IDSelectedStudio,
				needRefetchStudios: state.needRefetchStudios,
				nameOfVideo: state.nameOfVideo,
				fetchedCategories: state.fetchedCategories,
				IDSelectedCategory: state.IDSelectedCategory,
				fileData: state.fileData,
				fetchedLanguages: state.fetchedLanguages,
				IDsSelectedLanguages: state.IDsSelectedLanguages,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</CreateVideoContext.Provider>
	);
};

export default CreateVideoState;
